import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Drawer,
  Tooltip,
  TextField,
  InputAdornment,
  Fab,
  Badge,
  Zoom,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Menu as MenuIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Search as SearchIcon,
  InfoOutlined as InfoOutlinedIcon,
  QrCode as QrCodeIcon,
  ShoppingCart as ShoppingCartIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Build as BuildIcon,
  Add as AddIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBarcode } from '@fortawesome/free-solid-svg-icons';
import { useSelector, useDispatch } from 'react-redux';
import useStoreInfo from '../../../hooks/useCompanyInfo';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchSuppliers, addSupplierToStore } from '../../../app/store/slices/supplierSlice';
import { fetchProductBrands, addBrandToStore } from '../../../app/store/slices/productBrandSlice';
import { fetchProductCategories, addCategoryToStore } from '../../../app/store/slices/productCategorySlice';
import { fetchPaymentMethods } from '../../../app/store/slices/paymentMethodSlice';

import CategoryPanel from '../components/CategoryPanel';
import POSLayout from '../components/POSLayout';
import ProductGrid from '../components/ProductGrid';
import CartPanel from '../components/CartPanel';
import CheckoutDialog from '../components/CheckoutDialog';
import PreviewDialog from '../components/PreviewDialog';
import CustomerDialog from '../components/CustomerDialog';
import PrintOptionsDialog from '../components/PrintOptionsDialog';
import A4PreviewDialog from '../components/A4PreviewDialog';
import AddProductDialog from '../../product/components/AddProductDialog';
import AddCustomerDialog from '../../customers/components/AddCustomerDialog';
import MultiplePaymentMethodsSection from '../../invoices/components/MultiplePaymentMethodsSection';
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import UnsavedChangesDialog from '../../../components/UnsavedChangesDialog';
import RepairTicketDialog from '../components/RepairTicketDialog'; // ✅ Repair ticket dialog
import { calcTotals, calcVATBreakdown } from '../utils/totalsCalculator'; // ✅ Centralized calculation utility

import { searchProducts, getCategoriesWithProductCount, getProductsByCategory, searchBySKUOrBarcode } from '../../product/services/productService';
import { A4InvoiceTemplate } from '../components/A4InvoiceTemplate';
import { generateSaleTicketPreview, generateRepairTicketPreview } from '../utils/ticketPreviewUtils';
import { getVatRates } from '../../companysettings/services/vatService';
import { createTicket, validateTicketData, transformCartToTicketDto } from '../services/ticketDeCaisseService';
import TICKET_TYPE from '../../../constants/ticketTypes';
import { openCashDrawer, openCashDrawerSerial, addDrawerCommandToReceipt, shouldOpenDrawer } from '../utils/cashDrawerUtils';
import { printReceipt } from '../utils/printService';

function generateReceiptNumber() {
  const prefix = 'TKT';
  const timestamp = Date.now().toString().slice(-8);
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `${prefix}-${timestamp}${random}`;
}

const PosListPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const posContainerRef = useRef(null);

  const { storeInfo } = useStoreInfo();
  const { hasModulePermission, canCreateModule, modulePermissions } = useAuth(); // ✅ Get permission functions
  
  // ✅ Check if user has permission to create repair tickets (using Reparation module permission)
  // ✅ Directly check the permission without admin bypass for sub-modules
  const canCreateRepairTicket = modulePermissions?.['Reparation']?.CanView === true;
  
  // ✅ Get suppliers, brands, categories, and payment methods from Redux store
  const suppliersFromStore = useSelector((state) => state.suppliers.suppliers) || [];
  const brandsFromStore = useSelector((state) => state.productBrands.brands) || [];
  const categoriesFromStore = useSelector((state) => state.productCategories.categories) || [];
  const paymentMethodsFromStore = useSelector((state) => state.paymentMethod.paymentMethods) || [];

  // ✅ Sort payment methods alphabetically by name
  const sortedPaymentMethods = useMemo(() => {
    return [...paymentMethodsFromStore].sort((a, b) => {
      const nameA = (a.name || a.label || '').toUpperCase();
      const nameB = (b.name || b.label || '').toUpperCase();
      return nameA.localeCompare(nameB);
    });
  }, [paymentMethodsFromStore]);

  // State Management
  const [categories, setCategories] = useState([]);
  const [rawCategoriesWithProducts, setRawCategoriesWithProducts] = useState([]); // ✅ Store raw API data with products
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryPages, setCategoryPages] = useState({}); // Track current page for each category
  const [categoryProductsCache, setCategoryProductsCache] = useState({}); // Cache products for each category page
  const [cart, setCart] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [customVATRate, setCustomVATRate] = useState(21);
  const [isTvaIncluded, setIsTvaIncluded] = useState(true);
  const [vatRates, setVatRates] = useState([]);
  const searchInProgressRef = useRef(false); // Track if search is in progress to prevent overlaps
  
  // Dialog States
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [addCustomerDialogOpen, setAddCustomerDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [cartCollapsed, setCartCollapsed] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [publicSearch, setPublicSearch] = useState(false); // Toggle for barcode/SKU search
  const [initialProductData, setInitialProductData] = useState(null); // Pre-filled data for add product dialog
  const [searchErrorMessage, setSearchErrorMessage] = useState(''); // Error message to display under search bar
  const [repairTicketDialogOpen, setRepairTicketDialogOpen] = useState(false); // ✅ Repair ticket dialog

  // Payment State
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null); // ✅ Will store payment method object { Id, Name }
  const [paidAmount, setPaidAmount] = useState('');
  const [receiptNumber, setReceiptNumber] = useState(generateReceiptNumber());
  const [useMultiplePayments, setUseMultiplePayments] = useState(false); // ✅ Toggle for multi-payment
  const [paymentMethods, setPaymentMethods] = useState([]); // ✅ Array of {id, method, amount} for multi-payment
  const [walkInCustomerName, setWalkInCustomerName] = useState(''); // ✅ Walk-in customer name

  // Fullscreen State
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [printOptionsOpen, setPrintOptionsOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [currentFormat, setCurrentFormat] = useState('receipt');

  const [discount, setDiscount] = useState(null);
  const [notes, setNotes] = useState('-'); // ✅ Default notes
  const [notesDialogOpen, setNotesDialogOpen] = useState(false); // ✅ Notes dialog state
  const [serialNumberDialog, setSerialNumberDialog] = useState({ open: false, item: null, serialNumber: "" }); // ✅ Serial number dialog
  const isPaginatingRef = useRef(false); // ✅ Track pagination state to prevent filter conflicts
  const searchAbortRef = useRef(null); // ✅ Track pending search requests to allow cancellation
  const customerFromCheckoutRef = useRef(false); // ✅ Track if CustomerDialog opened from CheckoutDialog
  const customerFromRepairRef = useRef(false); // ✅ Track if CustomerDialog opened from RepairTicketDialog
  
  // ✅ Cash Drawer State
  const [openDrawerOnPrint, setOpenDrawerOnPrint] = useState(true); // ✅ Toggle to open drawer when printing

  // ✅ Checkout Dialog State
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);

  // ✅ Log store info when it changes
  useEffect(() => {
    if (storeInfo) {
    } else {

    }
  }, [storeInfo]);

  useEffect(() => {
    fetchProducts();

    getVatRates()
      .then((response) => {
        const rates = response?.payload || response || [];
        const normalizedRates = Array.isArray(rates)
          ? rates
              .map((rate) => Number(rate?.VATPercentage ?? rate?.vatPercentage ?? rate))
              .filter((rate) => Number.isFinite(rate))
          : [];
        setVatRates(normalizedRates);
      })
      .catch(() => setVatRates([]));
    
    // ✅ Fetch payment methods on mount
    if (paymentMethodsFromStore.length === 0) {
      dispatch(fetchPaymentMethods());
    }

    // Add fullscreen change event listener
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // No default payment method — user must select one explicitly.

  // ✅ Fetch categories (counts only) then load first category's products
  const fetchProducts = async () => {
    try {
      setLoading(true);
      
      // STEP 1: Get categories with product counts (lightweight, no products loaded)
      const response = await getCategoriesWithProductCount();
      
      const apiCategories = response?.payload?.Categories || response?.payload?.categories;
      
      if (!apiCategories || apiCategories.length === 0) {

        toast.warning(t('no_products_available') || 'No products available');
        setProducts([]);
        setFilteredProducts([]);
        setCategories([]);
        return;
      }

      // Sort categories ascending by name
      const sortedCats = [...apiCategories].sort((a, b) => {
        const nameA = (a.CategoryName || a.categoryName || '').toUpperCase();
        const nameB = (b.CategoryName || b.categoryName || '').toUpperCase();
        return nameA.localeCompare(nameB);
      });

      // Store raw data for RepairTicketDialog
      setRawCategoriesWithProducts(sortedCats);

      // Build categories UI array
      const mappedCategories = sortedCats.map(cat => {
        const count = cat.ProductCount || cat.productCount || cat.TotalProducts || cat.totalProducts || 0;
        return {
          id: cat.CategoryId || cat.categoryId,
          name: cat.CategoryName || cat.categoryName,
          count,
          hasMoreProducts: count > 20,
          icon: getCategoryIcon(cat.CategoryName || cat.categoryName),
          currentPage: 1,
          totalPages: Math.ceil(count / 20),
        };
      });

      setCategories(mappedCategories);

      // Initialize page tracking
      const pagesInit = {};
      mappedCategories.forEach(c => { pagesInit[c.id] = 1; });
      setCategoryPages(pagesInit);

      // STEP 2: Select first category and load its products
      if (mappedCategories.length > 0) {
        const firstCat = mappedCategories[0];
        setSelectedCategory(firstCat);
        await fetchCategoryProducts(firstCat.id, 1, firstCat);
      }
      
    } catch (error) {

      toast.error(t('failed_to_load_products') || 'Failed to load products');
      setProducts([]);
      setFilteredProducts([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch products for a specific category page using getproductbycategory
  const fetchCategoryProducts = async (categoryId, page = 1, categoryMeta = null) => {
    try {
      setLoading(true);

      const response = await getProductsByCategory(categoryId, page, 20);

      // Handle paginated response — try multiple shapes
      let items = [];
      const payload = response?.payload;
      if (payload?.Items) items = payload.Items;
      else if (payload?.items) items = payload.items;
      else if (Array.isArray(payload)) items = payload;
      else if (response?.Items) items = response.Items;
      else if (response?.items) items = response.items;
      else if (Array.isArray(response)) items = response;

      const cat = categoryMeta || categories.find(c => c.id === categoryId);
      const mapped = items.map(p => mapProductStructure(p, cat));

      // Cache page
      setCategoryProductsCache(prev => ({
        ...prev,
        [categoryId]: { ...prev[categoryId], [page]: mapped },
      }));

      setCategoryPages(prev => ({ ...prev, [categoryId]: page }));
      setProducts(mapped);
      setFilteredProducts(mapped);
    } catch (error) {

      toast.error(t('failed_to_load_products') || 'Failed to load products');
      setFilteredProducts([]);
    } finally {
      setLoading(false);
    }
  };
  
  // ✅ Helper function to map product structure
  const mapProductStructure = (product, category = null) => ({
    Id: product.id || product.Id,
    Name: product.name || product.Name,
    SKU: product.sku || product.SKU || product.barCode || product.BarCode,
    BarCode: product.barCode || product.BarCode,
    Description: product.description || product.Description,
    SalePrice: product.salePrice || product.SalePrice,
    CostPrice: product.costPrice || product.CostPrice,
    MinSalePrice: product.minSalePrice || product.MinSalePrice,
    AveragePrice: product.averagePrice || product.AveragePrice,
    StockQuantity: product.stockQuantity || product.StockQuantity,
    MinStockLevel: product.minStockLevel || product.MinStockLevel,
    IsActive: product.isActive !== undefined ? product.isActive : product.IsActive,
    IsPromotionProduct: product.isPromotionProduct !== undefined ? product.isPromotionProduct : product.IsPromotionProduct,
    PromotionPercentage: product.promotionPercentage || product.PromotionPercentage,
    ProductCategoryId: category ? (category.categoryId || category.CategoryId) : (product.productCategoryId || product.ProductCategoryId || product.CategoryId),
    ProductCategory: category ? {
      CategoryName: category.categoryName || category.CategoryName
    } : (product.productCategory || product.ProductCategory || (product.categoryName ? {
      CategoryName: product.categoryName
    } : null)),
    SupplierId: product.supplierId || product.SupplierId,
    Supplier: product.supplier || product.Supplier || (product.supplierName ? {
      Name: product.supplierName
    } : null),
    ProductBrandId: product.productBrandId || product.ProductBrandId || product.BrandId,
    ProductBrand: product.productBrand || product.ProductBrand || (product.brandName ? {
      BrandName: product.brandName
    } : null),
    ModelNumber: product.modelNumber || product.ModelNumber,
    ImageUrl: product.imageUrl || product.ImageUrl || product.image || product.Image || product.productImage || product.ProductImage,
    SyncToShopify: product.syncToShopify !== undefined ? product.syncToShopify : product.SyncToShopify,
    IsWishlistProduct: product.isWishlistProduct !== undefined ? product.isWishlistProduct : product.IsWishlistProduct,
    CreatedDate: product.createdDate || product.CreatedDate,
    ModifiedDate: product.modifiedDate || product.ModifiedDate,
    Price: product.salePrice || product.SalePrice,
    Stock: product.stockQuantity || product.StockQuantity,
  });



  const getCategoryIcon = (categoryName) => {
    const iconMap = {
      'GENERAL': '📦',
      'PC': '💻',
      'CLAVIERS': '⌨️',
      'ECRANS': '🖥️',
      'MEMOIRES': '💾',
      'DISQUE DUR': '💿',
      'SACS/HOUSSES': '💼',
      'ACCESSOIRES': '🔌',
      'ADAPTATEURS': '🔌',
      'RESEAU': '🌐',
      'BOULANGERIE': '🥖',
      'FRUITS': '🍎',
      'ELECTRONICS': '📱',
      'CLOTHING': '👕',
      'FOOD': '🍔',
      'DRINKS': '🥤',
      'BOOKS': '📚',
    };

    const upperCat = (categoryName || '').toUpperCase();
    for (const [key, icon] of Object.entries(iconMap)) {
      if (upperCat.includes(key)) {
        return icon;
      }
    }
    return '📦';
  };

  // ✅ FIXED: Update addToCart with proper rounding
  const addToCart = (product, quantity = 1) => {
    // Product price interpretation depends on TVA include toggle.
    const priceInclVAT = product.IsPriceEdited 
      ? product.SalePrice  // This is the edited price from ProductGrid
      : (product.SalePrice || product.Price || 0);
    
    const vatRate = customVATRate !== null ? customVATRate : 21;
    
    // If TVA is included, convert displayed price to base price (excl VAT).
    // If TVA is excluded, keep price as base price and VAT will be added on top.
    const basePrice = isTvaIncluded
      ? priceInclVAT / (1 + vatRate / 100)
      : priceInclVAT;

    // ✅ Check MinSalePrice validation - compare unit price (incl VAT) with minimum price
    const minPrice = product.MinSalePrice || 0;
    if (minPrice > 0 && priceInclVAT < minPrice) {
      toast.error(`Price €${priceInclVAT.toFixed(2)} is below minimum sale price €${minPrice.toFixed(2)} for ${product.Name}`);
      return; // Don't add to cart if below minimum
    }

    // ✅ Find existing item with same ID AND same price (to allow same product with different prices as separate line items)
    // If product has IsPriceEdited flag, always treat as new line item
    const existingIndex = product.IsPriceEdited 
      ? -1  // Force new line item for edited prices
      : cart.findIndex(item => {
          // Only merge if: same product ID AND price difference is negligible AND item was not price-edited
          if (item.Id !== product.Id) return false;
          if (item.IsPriceEdited) return false; // Don't merge with edited items
          
          const cartPrice = Number(item.PriceInclVAT) || 0;
          const newPrice = Number(priceInclVAT) || 0;
          const priceDiff = Math.abs(cartPrice - newPrice);
          
          return priceDiff < 0.01; // Prices must match within 1 cent
        });

    if (existingIndex !== -1) {
      // ✅ Product exists in cart with same price - update quantity only
      const updatedCart = [...cart];
      updatedCart[existingIndex].Quantity += quantity;
      toast.success(`${product.Name} ${t('quantity_updated') || 'quantity updated'}`);
      setCart(updatedCart);
    } else {
      // ✅ New product or same product with different price - add as new line item
      const newItem = {
        ...product,
        Quantity: quantity,
        Price: Number(basePrice.toFixed(4)), // ✅ Store with 4 decimal precision
        PriceInclVAT: Number(priceInclVAT.toFixed(2)),
        VAT: vatRate,
        OriginalPrice: product.IsPriceEdited ? product.OriginalPrice : priceInclVAT,
        IsPriceEdited: product.IsPriceEdited || false,
        SerialNumber: "", // ✅ Initialize serial number field
        _rowKey: `${product.Id}_${Date.now()}_${Math.random().toString(36).slice(2,7)}`, // ✅ Unique key
      };
      
      setCart([...cart, newItem]);
      
      // Show appropriate toast message
      if (product.IsPriceEdited) {
        toast.success(`${product.Name} added with custom price €${priceInclVAT.toFixed(2)}`);
      } else {
        toast.success(`${product.Name} ${t('added_to_cart') || 'added to cart'}`);
      }
    }
  };

  const updateCartQuantity = (rowKey, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(rowKey);
      return;
    }

    setCart(cart.map(item =>
      (item._rowKey === rowKey || item.Id === rowKey)
        ? { ...item, Quantity: newQuantity }
        : item
    ));
  };

  const removeFromCart = (rowKey) => {
    setCart(cart.filter(item => item._rowKey !== rowKey && item.Id !== rowKey));
    toast.info(t('item_removed') || 'Item removed');
  };

  const updateCartPrice = (rowKey, newPrice) => {
    setCart(cart.map(item => {
      if (item._rowKey === rowKey || item.Id === rowKey) {
        const vatRate = customVATRate !== null && customVATRate !== undefined ? customVATRate : (item.VAT || 0);
        if (isTvaIncluded) {
          // newPrice is incl VAT → derive excl price
          const newPriceExcl = Math.round(newPrice / (1 + vatRate / 100) * 100) / 100;
          return { ...item, PriceInclVAT: newPrice, Price: newPriceExcl };
        } else {
          // newPrice is incl VAT → derive excl price
          const newPriceExcl = Math.round(newPrice / (1 + vatRate / 100) * 100) / 100;
          return { ...item, Price: newPriceExcl, PriceInclVAT: newPrice };
        }
      }
      return item;
    }));
  };

  // Recalculates all cart item base prices when the TVA-included toggle changes.
  // PriceInclVAT is the "display price" that never changes; Price is the net base stored per item.
  const handleToggleTvaIncluded = (newValue) => {
    setIsTvaIncluded(newValue);
    setCart(prevCart =>
      prevCart.map(item => {
        const displayPrice = item.PriceInclVAT || 0;
        if (!displayPrice) return item;
        const vatRate = item.VAT != null ? item.VAT : (customVATRate || 21);
        const newBasePrice = newValue
          ? displayPrice / (1 + vatRate / 100)   // price shown includes VAT → extract net
          : displayPrice;                          // price shown is net → use directly
        return { ...item, Price: Number(newBasePrice.toFixed(4)) };
      })
    );
  };

  const clearCart = () => {
    setCart([]);
    setCustomer(null);
    setWalkInCustomerName(''); // ✅ Reset walk-in name
    setPaidAmount('');
    setDiscount(null);
    setCustomVATRate(21);
    setIsTvaIncluded(true);
    setNotes('-');
    setReceiptNumber(generateReceiptNumber());
    setSelectedPaymentMethod(null); // No default — user must select
    setUseMultiplePayments(false);
    setPaymentMethods([]);
    toast.info(t('cart_cleared') || 'Cart cleared');
  };
  
  // ✅ Serial number handlers
  const handleOpenSerialNumberDialog = (item) => {
    setSerialNumberDialog({ open: true, item, serialNumber: item.SerialNumber || "" });
  };

  const handleCloseSerialNumberDialog = () => {
    setSerialNumberDialog({ open: false, item: null, serialNumber: "" });
  };

  const handleSaveSerialNumber = () => {
    const { item, serialNumber } = serialNumberDialog;
    if (item) {
      setCart(
        cart.map((cartItem) =>
          cartItem._rowKey === item._rowKey
            ? { ...cartItem, SerialNumber: serialNumber }
            : cartItem
        )
      );
      toast.success(t('serial_number_saved') || 'Serial number saved');
    }
    handleCloseSerialNumberDialog();
  };
  
  // Handler for when a product is created from the dialog
  const handleProductCreated = (createdProduct) => {
    if (createdProduct && createdProduct.Id) {
      // Add the newly created product to cart
      addToCart(createdProduct, 1);
      toast.success(t('product_added_to_cart') || 'Product added to cart');
    }
  };

  // Handler for when a customer is created from the dialog
  const handleCustomerCreated = (createdCustomer) => {
    toast.success(t('customer_created_successfully') || 'Customer created successfully!');
    setAddCustomerDialogOpen(false);
    // Automatically set the newly created customer
    setCustomer(createdCustomer);
    // If the flow was triggered from CheckoutDialog, reopen it
    if (customerFromCheckoutRef.current) {
      customerFromCheckoutRef.current = false;
      setCheckoutDialogOpen(true);
    }
    // If the flow was triggered from RepairTicketDialog, reopen it
    if (customerFromRepairRef.current) {
      customerFromRepairRef.current = false;
      setRepairTicketDialogOpen(true);
    }
  };

  // ✅ Load suppliers, brands, and categories only when opening Add Product dialog
  const handleOpenProductDialog = (initialData = null) => {
    // Fetch suppliers, brands, and categories if not already loaded
    if (suppliersFromStore.length === 0) {
      dispatch(fetchSuppliers());
    }
    if (brandsFromStore.length === 0) {
      dispatch(fetchProductBrands());
    }
    if (categoriesFromStore.length === 0) {
      dispatch(fetchProductCategories());
    }
    setInitialProductData(initialData);
    setProductDialogOpen(true);
  };

  // ✅ Handle repair ticket submission
  const handleRepairTicketSubmit = async (repairTicketData) => {
    try {
      const { 
        services, 
        pickupDate, 
        customer, 
        paymentMethod, 
        paidAmount, 
        notes, 
        customerPhone, 
        templateHtml, 
        isTvaIncluded,
        useMultiplePayments = false,
        paymentMethodsArray = [],
      } = repairTicketData;

      // Convert services to cart format
      const repairCart = services.map((service, index) => {
        const originalPrice = parseFloat(service.Price) || 0;
        const vatRate = service.VAT || 21;
        
        // ✅ When VAT is included, convert to base price; otherwise price is already base price
        const basePrice = isTvaIncluded && originalPrice > 0
          ? originalPrice / (1 + vatRate / 100)
          : originalPrice;
        
        const priceInclVAT = isTvaIncluded 
          ? originalPrice 
          : originalPrice * (1 + vatRate / 100);
        
        return {
          Id: null, // No product ID for repair services
          ServiceName: service.Name,
          Name: service.Name,
          Description: service.Description || '',
          ServiceDescription: service.Description || '',
          SerialNumber: service.SerialNumber || '',
          Quantity: service.Quantity,
          Price: basePrice, // ✅ Base price (excl VAT)
          PriceInclVAT: priceInclVAT, // ✅ Price incl VAT
          VAT: vatRate,
          _rowKey: `repair-${Date.now()}-${index}`,
        };
      });

      // ✅ Use the receipt number from the dialog (backend may replace with sequential number)
      const { receiptNumber: dialogReceiptNumber } = repairTicketData;

      // Build ticket DTO parameters
      const ticketParams = {
        cart: repairCart,
        customer,
        walkInCustomerName: customer?.Name || customerPhone || '',
        customerPhone: customerPhone || customer?.PhoneNumber || '',
        // ✅ Only set payment method if there's a paid amount (and not using multiple payments)
        selectedPaymentMethod: paidAmount && paidAmount > 0 && !useMultiplePayments ? paymentMethod : null,
        paidAmount: paidAmount && paidAmount > 0 ? paidAmount : null,
        discount: repairTicketData.discount || null,
        customVATRate: repairTicketData.customVATRate || 21,
        receiptNumber: dialogReceiptNumber, // Use generated receipt number from dialog
        notes: notes || '',
        useMultiplePayments: useMultiplePayments || false,
        paymentMethods: paymentMethodsArray || [],
        allPaymentMethods: sortedPaymentMethods, // ✅ Pass full payment methods list
        ticketType: TICKET_TYPE.REPAIR, // ✅ Repair ticket
        pickupDate: pickupDate, // ✅ Pickup date
        isTvaIncluded: repairTicketData.isTvaIncluded !== undefined ? repairTicketData.isTvaIncluded : true,
        // ✅ Pass pre-calculated totals from RepairTicketDialog
        preCalculatedTotals: {
          subtotal: repairTicketData.subtotal,
          discountAmount: repairTicketData.discountAmount,
          totalVAT: repairTicketData.vatAmount,
          totalIncl: repairTicketData.total,
        },
      };

      // Transform to ticket DTO
      const { dto: ticketDto, amountInChange } = transformCartToTicketDto(ticketParams);

      // Validate
      const validation = validateTicketData(ticketDto);
      if (!validation.isValid) {

        toast.error(`Validation failed: ${validation.errors.join(', ')}`);
        throw new Error('Validation failed');
      }

      // Create ticket
      const createdTicket = await createTicket(ticketDto);
      
      // ✅ Extract the ticket number from the response
      const backendTicketNumber = createdTicket?.payload?.TicketNumber || 
                                   createdTicket?.data?.payload?.TicketNumber ||
                                   createdTicket?.TicketNumber ||
                                   'REPAIR';
      
      toast.success(t('repair_ticket_created_success') || `Repair ticket #${backendTicketNumber} created successfully`);

      // Close dialog
      setRepairTicketDialogOpen(false);

      // ✅ Print the ticket using jsPDF approach (same as sale tickets)
      if (templateHtml) {
        try {
          const { jsPDF } = await import('jspdf');
          const html2canvas = (await import('html2canvas')).default;

          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = templateHtml;
          tempDiv.style.position = 'absolute';
          tempDiv.style.left = '-9999px';
          tempDiv.style.width = '80mm';
          tempDiv.style.background = 'white';
          tempDiv.style.padding = '10px';
          document.body.appendChild(tempDiv);

          const canvas = await html2canvas(tempDiv, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
          });

          document.body.removeChild(tempDiv);

          const imgData = canvas.toDataURL('image/png');
          const pdfHeight = (canvas.height * 80) / canvas.width;
          const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: [80, pdfHeight],
          });
          pdf.addImage(imgData, 'PNG', 0, 0, 80, pdfHeight);

          pdf.autoPrint();
          window.open(pdf.output('bloburl'), '_blank');

          toast.success(t('print_sent') || 'Print job sent successfully');
        } catch (printError) {

          // Don't fail the entire operation if printing fails
          toast.warning(t('print_failed') || 'Ticket created but printing failed');
        }
      }

    } catch (error) {

      toast.error(t('repair_ticket_creation_failed') || 'Failed to create repair ticket');
      throw error; // Re-throw so RepairTicketDialog knows it failed
    }
  };

  const handleCategorySelect = async (category) => {
    setSelectedCategory(category);
    setSearchQuery(''); // Clear search when changing category

    // Check cache first
    const cachedPage = categoryProductsCache[category.id]?.[1];
    if (cachedPage) {
      setCategoryPages(prev => ({ ...prev, [category.id]: 1 }));
      setProducts(cachedPage);
      setFilteredProducts(cachedPage);
      return;
    }

    // Fetch page 1 for the selected category
    await fetchCategoryProducts(category.id, 1, category);
  };
  
  // ✅ Handle category pagination
  const handleCategoryPageChange = async (categoryId, direction) => {
    const currentPage = categoryPages[categoryId] || 1;
    const category = categories.find(c => c.id === categoryId);
    
    if (!category) return;
    
    let newPage = currentPage;
    if (direction === 'next' && currentPage < category.totalPages) {
      newPage = currentPage + 1;
    } else if (direction === 'prev' && currentPage > 1) {
      newPage = currentPage - 1;
    } else {
      return; // No change needed
    }
    
    // ✅ Set pagination flag to prevent filter conflicts
    isPaginatingRef.current = true;
    
    // Check if we already have this page cached
    if (categoryProductsCache[categoryId]?.[newPage]) {
      const cached = categoryProductsCache[categoryId][newPage];
      setCategoryPages(prev => ({ ...prev, [categoryId]: newPage }));
      setProducts(cached);
      setFilteredProducts(cached);
      setTimeout(() => { isPaginatingRef.current = false; }, 100);
      return;
    }
    
    // Fetch new page
    try {
      await fetchCategoryProducts(categoryId, newPage, category);
    } finally {
      setTimeout(() => { isPaginatingRef.current = false; }, 100);
    }
  };

  const handlePreview = () => {
    if (cart.length === 0) {
      toast.error(t('please_add_products') || 'Please add at least one product');
      return;
    }
    
    
    setPrintOptionsOpen(true); // ✅ Open print options dialog instead
  };

  const handlePrint = () => {
    if (cart.length === 0) {
      toast.error(t('please_add_products') || 'Please add at least one product');
      return;
    }
    
    setPrintOptionsOpen(true); // ✅ Open print options dialog instead
  };

  // ✅ NEW: Handler to manually open cash drawer
  const handleOpenCashDrawer = async () => {
    try {
      
      // For USB printers, use the print driver method
      const success = await openCashDrawer();
      
      if (success) {
        toast.success(t('cash_drawer_opened') || 'Cash drawer opened');
      } else {
        toast.error(t('failed_to_open_drawer') || 'Failed to open cash drawer');
      }
    } catch (error) {

      toast.error(t('failed_to_open_drawer') || 'Failed to open cash drawer');
    }
  };

  // ✅ Update handleFormatSelection to handle both preview and print correctly
  const handleFormatSelection = async (format, action) => {
    setPrintOptionsOpen(false);
    setCurrentFormat(format);

    // ── Convert to Invoice ──────────────────────────────────────────────────
    if (format === 'invoice') {
      if (cart.length === 0) {
        toast.error(t('please_add_products') || 'Please add at least one product');
        return;
      }

      // Map cart items → invoiceProducts structure expected by AddInvoicePage
      const mappedProducts = cart.map((item, index) => {
        const basePrice = parseFloat(item.Price) || 0;
        const qty = item.Quantity || 1;
        const vatRate = item.VAT != null ? item.VAT : (customVATRate || 21);
        const totalExcl = qty * basePrice;
        const vatAmount = totalExcl * vatRate / 100;
        const totalIncl = totalExcl + vatAmount;
        return {
          Id: item.Id,
          Name: item.Name,
          Quantity: qty,
          Price: basePrice.toFixed(2),
          VAT: vatRate,
          TotalExcl: totalExcl.toFixed(2),
          VATAmount: vatAmount.toFixed(2),
          TotalIncl: totalIncl.toFixed(2),
          TotalVAT: vatAmount.toFixed(2),
          TotalInclVAT: totalIncl.toFixed(2),
          SerialNumber: item.SerialNumber || '',
          _rowKey: item._rowKey || Date.now() + index,
        };
      });

      // Map customer → AsyncSelect value shape used by AddInvoicePage
      const mappedCustomer = customer
        ? {
            Id: customer.Id,
            Name: customer.Name || customer.CompanyName,
            CompanyName: customer.CompanyName || customer.Name,
            Email: customer.Email,
            PhoneNumber: customer.PhoneNumber,
            VATNumber: customer.VATNumber,
            Street: customer.Street,
            City: customer.City,
            PostCode: customer.PostCode,
            Country: customer.Country,
            label: customer.CompanyName || customer.Name,
            value: customer.Id,
          }
        : null;

      navigate('/invoices/add', {
        state: {
          fromPos: {
            products: mappedProducts,
            customer: mappedCustomer,
            discount: discount
              ? { type: discount.type, value: discount.value }
              : null,
            vatRate: customVATRate,
            isTvaIncluded,
            paymentMethodId: selectedPaymentMethod?.Id || null,
            paymentMethodName: selectedPaymentMethod?.Name || null,
            notes: notes || '',
          },
        },
      });
      return;
    }

    // ── Receipt / A4 preview & print ────────────────────────────────────────

    // Check if storeInfo is properly loaded
    if (!storeInfo) {

      toast.error('Store information not loaded. Please refresh the page.');
      return;
    }

    let templateHtml;

    if (format === 'a4') {
      templateHtml = A4InvoiceTemplate(
        cart,
        customer,
        storeInfo,
        selectedPaymentMethod,
        paidAmount,
        receiptNumber,
        discount,
        customVATRate,
        notes, // ✅ Pass notes
        useMultiplePayments, // ✅ Pass multi-payment flag
        paymentMethods, // ✅ Pass payment methods array
        sortedPaymentMethods, // ✅ Pass all payment methods for lookup
        walkInCustomerName // ✅ Walk-in customer name
      );
    } else {
      templateHtml = generateSaleTicketPreview({
        cart,
        customer,
        storeInfo,
        receiptNumber,
        paidAmount,
        paymentMethod: selectedPaymentMethod ? { name: selectedPaymentMethod } : null,
        allPaymentMethods: sortedPaymentMethods,
        useMultiplePayments,
        paymentMethodsArray: paymentMethods,
        discount,
        customVATRate,
        notes,
        walkInCustomerName,
        isTvaIncluded,
      });
    }


    if (action === 'preview') {
      setPreviewHtml(templateHtml);
      setPreviewOpen(true);
    } else if (action === 'print') {
      await saveAndPrintDocument(templateHtml, format); // ✅ Use new function that saves first
    }
  };

  // ✅ Checkout Dialog finalizer — receives validated form data + pre-built template HTML from the dialog
  // ── Convert to Invoice from Checkout Dialog ────────────────────────────────
  const handleConvertToInvoice = (localData) => {
    if (cart.length === 0) {
      toast.error(t('please_add_products') || 'Please add at least one product');
      return;
    }

    // Map cart items → invoiceProducts structure
    const mappedProducts = cart.map((item, index) => {
      const basePrice = parseFloat(item.Price) || 0;
      const qty = item.Quantity || 1;
      const vatRate = item.VAT != null ? item.VAT : (customVATRate || 21);
      const totalExcl = qty * basePrice;
      const vatAmount = totalExcl * vatRate / 100;
      const totalIncl = totalExcl + vatAmount;
      return {
        Id: item.Id,
        Name: item.Name,
        Quantity: qty,
        Price: basePrice.toFixed(2),
        VAT: vatRate,
        TotalExcl: totalExcl.toFixed(2),
        VATAmount: vatAmount.toFixed(2),
        TotalIncl: totalIncl.toFixed(2),
        TotalVAT: vatAmount.toFixed(2),
        TotalInclVAT: totalIncl.toFixed(2),
        SerialNumber: item.SerialNumber || '',
        _rowKey: item._rowKey || Date.now() + index,
      };
    });

    // Use localData.customer (don't fall back to parent customer to avoid stale state)
    const activeCustomer = localData?.customer;
    const mappedCustomer = activeCustomer
      ? {
          Id: activeCustomer.Id,
          Name: activeCustomer.Name || activeCustomer.CompanyName,
          CompanyName: activeCustomer.CompanyName || activeCustomer.Name,
          Email: activeCustomer.Email,
          PhoneNumber: activeCustomer.PhoneNumber,
          VATNumber: activeCustomer.VATNumber,
          CustomerPeppolId: activeCustomer.CustomerPeppolId || activeCustomer.PeppolId,
          Street: activeCustomer.Street,
          City: activeCustomer.City,
          PostCode: activeCustomer.PostCode,
          Country: activeCustomer.Country,
          label: activeCustomer.CompanyName || activeCustomer.Name,
          value: activeCustomer.Id,
        }
      : null;

    // Use localData payment method if available
    const activePaymentMethod = localData?.paymentMethod || selectedPaymentMethod;
    const activeUseMultiple = localData?.useMultiple ?? useMultiplePayments;
    const activePaymentMethodsArray = localData?.paymentMethodsArray || paymentMethods;
    const activePaidAmount = localData?.paidAmount || paidAmount;
    const activeWalkInName = localData?.walkInCustomerName || walkInCustomerName;

    // Calculate totals for paid/due amounts
    const { totalIncl } = calcTotals(cart, discount, customVATRate, isTvaIncluded);
    const parsedPaidAmount = parseFloat(activePaidAmount) || 0;
    const dueAmount = Math.max(totalIncl - parsedPaidAmount, 0);

    navigate('/invoices/add', {
      state: {
        fromPos: {
          products: mappedProducts,
          customer: mappedCustomer,
          discount: discount ? { type: discount.type, value: discount.value, amount: discount.amount } : null,
          vatRate: customVATRate,
          isTvaIncluded,
          paymentMethodId: activePaymentMethod?.Id || null,
          paymentMethodName: activePaymentMethod?.Name || null,
          paidAmount: activePaidAmount,
          dueAmount: dueAmount,
          useMultiplePayments: activeUseMultiple,
          paymentMethodsArray: activePaymentMethodsArray,
          walkInCustomerName: activeWalkInName || '',
        },
      },
    });
  };

  const handleCheckoutFinalize = async ({ format, action, localData, templateHtml }) => {
    const {
      paymentMethod,
      paidAmount: localPaid,
      notes: localNotes,
      useMultiple,
      paymentMethodsArray: localPMs,
      walkInCustomerName: localWalkIn,
      customer: localCustomer,
      openDrawerOnPrint: localOpenDrawer,
      calculatedTotals, // ✅ Pre-calculated totals from CheckoutDialog
    } = localData;

    // Sync state so CartPanel stays in sync
    setSelectedPaymentMethod(paymentMethod);
    setPaidAmount(localPaid);
    setNotes(localNotes);
    setUseMultiplePayments(useMultiple);
    setPaymentMethods(localPMs);
    if (localWalkIn !== undefined) setWalkInCustomerName(localWalkIn);
    if (localCustomer !== undefined) setCustomer(localCustomer);
    if (localOpenDrawer !== undefined) setOpenDrawerOnPrint(localOpenDrawer);

    setCheckoutDialogOpen(false);

    if (action === 'preview') {
      setCurrentFormat(format);
      setPreviewHtml(templateHtml);
      setPreviewOpen(true);
      return;
    }

    // action === 'print' or 'register_only'
    setCurrentFormat(format);
    try {
      const ticketParams = {
        cart,
        customer,
        walkInCustomerName: localWalkIn ?? walkInCustomerName,
        customerPhone: localCustomer?.PhoneNumber || customer?.PhoneNumber || '',
        selectedPaymentMethod: paymentMethod,
        paidAmount: localPaid,
        discount,
        customVATRate,
        receiptNumber,
        notes: localNotes,
        useMultiplePayments: useMultiple,
        paymentMethods: localPMs,
        allPaymentMethods: sortedPaymentMethods, // ✅ Pass full payment methods list
        ticketType: TICKET_TYPE.SALE, // ✅ Sale ticket
        isTvaIncluded, // ✅ Pass TVA inclusion flag for correct calculation
        preCalculatedTotals: calculatedTotals, // ✅ Pass pre-calculated totals from dialog
      };
      const { dto: ticketDto, amountInChange } = transformCartToTicketDto(ticketParams);
      const validation = validateTicketData(ticketDto);
      if (!validation.isValid) {

        toast.error(`Validation failed: ${validation.errors.join(', ')}`);
        return;
      }
      try {
        await createTicket(ticketDto);
        toast.success(`Ticket #${receiptNumber} saved successfully`);
      } catch (err) {
        toast.error(`Failed to save ticket: ${err.message || 'Unknown error'}`);
        return;
      }
      // Clear cart immediately so POS is ready for next sale
      clearCart();
      // Only print if not register_only
      if (action !== 'register_only') {
        printDocumentOnly(templateHtml, format).catch(() => {});
      }
    } catch (error) {
      toast.error(t('print_failed') || 'Failed to complete checkout');
    }
  };

  // ✅ NEW: Function to save ticket and then print (used when clicking print from print options)
  const saveAndPrintDocument = async (templateHtml, format) => {
    try {
      // ✅ STEP 1: Save ticket to database
      
      const ticketParams = {
        cart,
        customer,
        walkInCustomerName, // ✅ Walk-in customer name
        customerPhone: customer?.PhoneNumber || customer?.Phone || '',
        selectedPaymentMethod: selectedPaymentMethod, // ✅ Send complete payment method object {Id, Name}
        paidAmount,
        discount,
        customVATRate,
        receiptNumber,
        notes: notes || '-', // ✅ Use user's notes
        useMultiplePayments, // ✅ Multi-payment flag
        paymentMethods, // ✅ Array of payment methods for multi-payment
        allPaymentMethods: sortedPaymentMethods, // ✅ Pass full payment methods list
        ticketType: TICKET_TYPE.SALE, // ✅ Sale ticket
        isTvaIncluded, // ✅ Pass TVA inclusion flag for correct calculation
        preCalculatedTotals: null, // ✅ No pre-calculated totals when printing from preview
      };

      const { dto: ticketDto, amountInChange } = transformCartToTicketDto(ticketParams);
      const validation = validateTicketData(ticketDto);

      if (!validation.isValid) {

        toast.error(`Validation failed: ${validation.errors.join(', ')}`);
        return;
      }

      let createdTicket;
      try {
        createdTicket = await createTicket(ticketDto);
        toast.success(`Ticket #${receiptNumber} saved successfully`);
      } catch (error) {

        toast.error(`Failed to save ticket: ${error.message || 'Unknown error'}`);
        return;
      }

      // ✅ STEP 2: Print the document
      await printDocumentOnly(templateHtml, format);

      // ✅ STEP 3: Clear cart after successful print and save
      clearCart();

    } catch (error) {

      toast.error(t('print_failed') || 'Failed to print and save document');
    }
  };

  // ✅ Print function — silently via QZ Tray, falls back to browser dialog
  const printDocumentOnly = async (templateHtml, format) => {
    try {
      // Print using a hidden iframe with srcdoc (same approach as InvoicePreviewDialog)
      const printIframe = document.createElement('iframe');
      printIframe.style.position = 'fixed';
      printIframe.style.left = '-9999px';
      printIframe.style.top = '-9999px';
      printIframe.style.width = format === 'a4' ? '210mm' : '80mm';
      printIframe.style.height = '0';
      printIframe.style.border = 'none';

      // Use srcdoc to avoid document.write which triggers unload policy violation
      printIframe.srcdoc = templateHtml;

      // Wait for content to render, then trigger print
      printIframe.onload = () => {
        setTimeout(() => {
          try {
            printIframe.contentWindow.focus();
            printIframe.contentWindow.print();
          } catch (e) {

          }
          // Clean up after print dialog closes
          setTimeout(() => {
            if (printIframe.parentNode) {
              printIframe.parentNode.removeChild(printIframe);
            }
          }, 3000);
        }, 300);
      };

      document.body.appendChild(printIframe);

      toast.success(t('print_sent') || 'Print job sent successfully');

      if (previewOpen) setPreviewOpen(false);
    } catch (error) {

      toast.error(t('print_failed') || 'Failed to print document');
      throw error;
    }
  };

  // ✅ This is called from preview dialog's print button - saves ticket, prints, and clears cart
  const printDocument = async (templateHtml, format) => {
    try {
      // ✅ STEP 1: Save ticket to database
      
      const ticketParams = {
        cart,
        customer,
        walkInCustomerName, // ✅ Walk-in customer name
        customerPhone: customer?.PhoneNumber || customer?.Phone || '',
        selectedPaymentMethod: selectedPaymentMethod,
        paidAmount,
        discount,
        customVATRate,
        receiptNumber,
        notes: notes || '-',
        useMultiplePayments, // ✅ Multi-payment flag
        paymentMethods, // ✅ Array of payment methods for multi-payment
        allPaymentMethods: sortedPaymentMethods, // ✅ Pass full payment methods list
        ticketType: TICKET_TYPE.SALE, // ✅ Sale ticket
        isTvaIncluded, // ✅ Pass TVA inclusion flag for correct calculation
      };

      const { dto: ticketDto, amountInChange } = transformCartToTicketDto(ticketParams);
      const validation = validateTicketData(ticketDto);

      if (!validation.isValid) {

        toast.error(`Validation failed: ${validation.errors.join(', ')}`);
        return;
      }

      let createdTicket;
      try {
        createdTicket = await createTicket(ticketDto);
        toast.success(`Ticket #${receiptNumber} saved successfully`);
      } catch (error) {

        toast.error(`Failed to save ticket: ${error.message || 'Unknown error'}`);
        return;
      }

      // ✅ STEP 2: Print the document
      await printDocumentOnly(templateHtml, format);

      // ✅ STEP 3: Clear cart after successful print and save
      clearCart();

    } catch (error) {

      toast.error(t('print_failed') || 'Failed to print and save document');
    }
  };

  // ✅ Updated getReceiptTemplate to use company info from Redux and centralized calculation utility
  const getReceiptTemplate = () => {
    // Use centralized calculation utility for consistency
    const calculatedTotals = calcTotals(cart, discount, null, isTvaIncluded);
    const totalExcl = calculatedTotals.subtotal;
    const totalVAT = calculatedTotals.totalVAT;
    const totalIncl = calculatedTotals.totalIncl;
    const discountAmount = calculatedTotals.discountAmount;
    const change = (parseFloat(paidAmount) || 0) - totalIncl;

    // Calculate VAT breakdown using centralized utility
    const vatBreakdown = calcVATBreakdown(cart, discountAmount, null, isTvaIncluded);

    let vatSectionHtml = '';
    Object.keys(vatBreakdown).forEach((rate) => {
      const { base, vat } = vatBreakdown[rate];
      vatSectionHtml += `
        <tr>
          <td style="padding: 2px 0;">VAT ${rate}%:</td>
          <td style="text-align: right;">€${base.toFixed(2)}</td>
          <td style="text-align: right;">€${vat.toFixed(2)}</td>
        </tr>
      `;
    });

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            line-height: 1.4;
            color: #000;
            background: white;
            padding: 10px;
          }
          .receipt-container { width: 100%; }
          .header { text-align: center; margin-bottom: 15px; border-bottom: 2px dashed #000; padding-bottom: 10px; }
          .header h1 { font-size: 18px; font-weight: 600; margin-bottom: 5px; }
          .header p { font-size: 10px; margin: 2px 0; }
          .info-section { margin: 10px 0; font-size: 11px; }
          .info-row { display: flex; justify-content: space-between; margin: 3px 0; }
          table { width: 100%; border-collapse: collapse; margin: 10px 0; }
          th { text-align: left; border-bottom: 1px solid #000; padding: 5px 0; font-size: 11px; }
          td { padding: 5px 0; font-size: 11px; }
          .item-row td { border-bottom: 1px dotted #ccc; }
          .total-section { margin-top: 15px; border-top: 2px solid #000; padding-top: 10px; }
          .total-row { display: flex; justify-content: space-between; margin: 5px 0; font-weight: 600; }
          .grand-total { font-size: 16px; margin-top: 10px; }
          .footer { text-align: center; margin-top: 20px; border-top: 2px dashed #000; padding-top: 10px; font-size: 10px; }
          .payment-info { margin: 10px 0; padding: 10px; background: #f5f5f5; border: 1px solid #ddd; }
        </style>
      </head>
      <body>
        <div class="receipt-container">
          <div class="header">
            <h1>TICKET DE CAISSE</h1>
            <p>${storeInfo?.Name || 'Your Store Name'}</p>
            <p>${storeInfo?.Address || '123 Street Address, City'}</p>
            <p>Tel: ${storeInfo?.Phone || storeInfo?.PhoneNumber || '+32 123 456 789'}</p>
            <p>TVA: ${storeInfo?.VATNumber || storeInfo?.TaxNumber || 'BE 0123.456.789'}</p>
            ${storeInfo?.Email ? `<p>Email: ${storeInfo.Email}</p>` : ''}
            ${storeInfo?.Website ? `<p>${storeInfo.Website}</p>` : ''}
          </div>

          <div class="info-section">
            <div class="info-row">
              <span>Receipt #:</span>
              <span><strong>${receiptNumber}</strong></span>
            </div>
            <div class="info-row">
              <span>Date:</span>
              <span>${new Date().toLocaleDateString('fr-BE')} ${new Date().toLocaleTimeString('fr-BE')}</span>
            </div>
            ${customer ? `
              <div class="info-row">
                <span>Client:</span>
                <span><strong>${customer.CompanyName || customer.Name || 'N/A'}</strong></span>
              </div>
            ` : ''}
            <div class="info-row">
              <span>Cashier:</span>
              <span>Admin</span>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th style="text-align: center;">Qty</th>
                <th style="text-align: right;">Price</th>
                <th style="text-align: right;">Total</th>
              </tr>
            </thead>
            <tbody>
              ${cart.map(item => `
                <tr class="item-row">
                  <td>${item.Name || 'N/A'}</td>
                  <td style="text-align: center;">${item.Quantity}</td>
                  <td style="text-align: right;">€${item.Price.toFixed(2)}</td>
                  <td style="text-align: right;">€${(item.Quantity * item.Price).toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="total-section">
            <div class="total-row">
              <span>Subtotal (Excl. VAT):</span>
              <span>€${totalExcl.toFixed(2)}</span>
            </div>
            
            ${discount && discountAmount > 0 ? `
            <div class="total-row" style="color: #d32f2f; font-weight: bold;">
              <span>Discount ${discount.type === 'percentage' ? `(${discount.value}%)` : ''}:</span>
              <span>-€${discountAmount.toFixed(2)}</span>
            </div>
            ` : ''}
            
            <table style="font-size: 10px; margin: 10px 0;">
              <thead>
                <tr>
                  <th>VAT Rate</th>
                  <th style="text-align: right;">Base</th>
                  <th style="text-align: right;">VAT Amount</th>
                </tr>
              </thead>
              <tbody>
                ${vatSectionHtml}
              </tbody>
            </table>

            <div class="total-row">
              <span>Total VAT:</span>
              <span>€${totalVAT.toFixed(2)}</span>
            </div>
            
            <div class="total-row grand-total">
              <span>TOTAL (Incl. VAT):</span>
              <span>€${totalIncl.toFixed(2)}</span>
            </div>
          </div>

          <div class="payment-info">
            ${paidAmount ? `
              <div class="total-row">
                <span>Payment Method:</span>
                <span>${selectedPaymentMethod?.Name || selectedPaymentMethod || 'N/A'}</span>
              </div>
            ` : ''}
            <div class="total-row">
              <span>Paid:</span>
              <span>€${parseFloat(paidAmount || 0).toFixed(2)}</span>
            </div>
            ${paidAmount ? `
              <div class="total-row">
                <span>Change:</span>
                <span>€${change.toFixed(2)}</span>
              </div>
            ` : ''}
          </div>

          <div class="footer">
            <p>${notes || '-'}</p>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // ✅ Discount operations
  const handleApplyDiscount = (discountData) => {
    setDiscount(discountData);
    toast.success(
      `${t('discount_applied') || 'Discount applied'}: ${
        discountData.type === 'percentage'
          ? `${discountData.value}%`
          : `€${discountData.value.toFixed(2)}`
      }`
    );
  };

  const handleRemoveDiscount = () => {
    setDiscount(null);
    toast.info(t('discount_removed') || 'Discount removed');
  };

  // ✅ Notes operations
  const handleSaveNotes = (newNotes) => {
    setNotes(newNotes);
    setNotesDialogOpen(false);
    toast.success(t('notes_saved') || 'Notes saved successfully');
  };

  // VAT handler
  const handleApplyVAT = (vatRate) => {
    if (vatRate === null || vatRate === undefined) {
      setCustomVATRate(21); // ✅ Reset to 21% instead of 0
      
      // Update all cart items to 21% VAT and recalculate base prices
      const updatedCart = cart.map(item => {
        const priceInclVAT = item.PriceInclVAT || item.Price * (1 + item.VAT / 100);
        const newBasePrice = priceInclVAT / (1 + 21 / 100);
        return {
          ...item,
          Price: newBasePrice,
          VAT: 21,
        };
      });
      setCart(updatedCart);
      
      toast.info(t('vat_reset') || 'VAT rate reset to 21%');
    } else {
      setCustomVATRate(vatRate);
      
      // Update VAT for all items and recalculate base prices
      const updatedCart = cart.map(item => {
        const priceInclVAT = item.PriceInclVAT || item.Price * (1 + item.VAT / 100);
        const newBasePrice = priceInclVAT / (1 + vatRate / 100);
        return {
          ...item,
          Price: newBasePrice,
          VAT: vatRate,
        };
      });
      setCart(updatedCart);
      
      toast.success(t('vat_applied') || `VAT rate changed to ${vatRate}%`);
    }
  };

  // ✅ Search via API: calls product/search?name= OR search-by-sku-barcode based on publicSearch toggle
  const handleSearch = useCallback(async (searchValue, signal) => {
    // Prevent overlapping searches
    if (searchInProgressRef.current) {
      return;
    }

    if (!searchValue || !searchValue.trim()) {
      setFilteredProducts(products);
      setSearchErrorMessage(''); // Clear error when search is cleared
      return;
    }

    const query = searchValue.trim();
    searchInProgressRef.current = true; // Mark search as in progress

    try {
      // Public search (barcode/SKU) - open add product dialog with pre-filled data
      if (publicSearch) {
        setSearchErrorMessage(''); // Clear previous errors
        try {
          const response = await searchBySKUOrBarcode(query);
          
          // Abort if a newer search has been initiated
          if (signal?.aborted) return;

          // Check for failure response first (200 OK but status: "Failure")
          if (response?.status === "Failure" || response?.data?.status === "Failure" || 
              response?.payload?.status === "Failure") {
            const errorMsg = response?.message || response?.data?.message || 
                            response?.payload?.message || 
                            t('no_product_found') || 'No product found with this input';
            setSearchErrorMessage(errorMsg);
            setFilteredProducts([]);
            return; // Stop processing
          }

          // Extract product data from response
          let productData = response?.payload || response?.data || response;

          // Check again for failure status in productData
          if (productData?.status === "Failure") {
            const errorMsg = productData?.message || t('no_product_found') || 'No product found with this input';
            setSearchErrorMessage(errorMsg);
            setFilteredProducts([]);
            return; // Stop processing
          }

          if (productData && productData.Name) {
            // Open add product dialog with pre-filled data
            handleOpenProductDialog({
              name: productData.Name || '',
              description: productData.Description || '',
              sku: productData.SKU || '',
              barcode: productData.BarCode || '',
              modelNumber: productData.ModelNumber || '',
            });
            // Clear search and error after opening dialog
            setSearchQuery('');
            setSearchErrorMessage('');
          } else {
            setFilteredProducts([]);
            setSearchErrorMessage(t('no_product_found') || 'No product found');
          }
        } catch (error) {
          if (signal?.aborted) return;

          
          // Extract error message from API response
          let errorMessage = '';
          if (error?.response?.status === 404) {
            // 404 - Not found, display the message from API
            errorMessage = error?.response?.data?.message || 
                          error?.message?.message ||
                          error?.message ||
                          t('no_product_found') || 
                          'No product found with this input';
          } else {
            // Other errors - show generic error and use toast
            errorMessage = t('search_failed') || 'Failed to search product';
            toast.error(errorMessage);
          }
          
          setSearchErrorMessage(errorMessage);
          setFilteredProducts([]);
        }
        return;
      }

      // Regular product search
      setSearchErrorMessage(''); // Clear previous errors
      
      // Require at least 2 chars to avoid overly broad searches
      if (query.length < 2) {
        setFilteredProducts(products.slice(0, 20));
        return;
      }

      // Don't set global loading to avoid blocking UI during search

      // Detect barcode/SKU input and send as skuBarCode, otherwise search by name
      const barcodeLengths = [8, 12, 13, 14];
      let searchParams;
      if (barcodeLengths.includes(query.length) && /^\d+$/.test(query)) {
        searchParams = { skuBarCode: query };
      } else if (/^\d+$/.test(query)) {
        searchParams = { skuBarCode: query };
      } else {
        searchParams = { name: query };
      }

      const response = await searchProducts(searchParams);

      // Abort if a newer search has been initiated
      if (signal?.aborted) return;

      // Check for failure response first (200 OK but status: "Failure")
      if (response?.status === "Failure" || response?.data?.status === "Failure" || 
          response?.payload?.status === "Failure") {
        const errorMsg = response?.message || response?.data?.message || 
                        response?.payload?.message || 
                        t('no_products_found') || 'No products found';
        setSearchErrorMessage(errorMsg);
        setFilteredProducts([]);
        return; // Stop processing
      }

      let dbResults = [];
      if (response?.payload?.Items) {
        dbResults = response.payload.Items;
      } else if (response?.payload?.items) {
        dbResults = response.payload.items;
      } else if (response?.Items) {
        dbResults = response.Items;
      } else if (response?.items) {
        dbResults = response.items;
      } else if (response?.payload && Array.isArray(response.payload)) {
        dbResults = response.payload;
      } else if (response?.data && Array.isArray(response.data)) {
        dbResults = response.data;
      } else if (Array.isArray(response)) {
        dbResults = response;
      }

      // Map database results to match POS structure
      const mappedResults = dbResults.map(product => mapProductStructure(product));

      if (mappedResults.length > 0) {
        // DON'T update products array to prevent triggering handleSearch recreation
        // Just update filtered products to show search results
        setFilteredProducts(mappedResults);
      } else {
        setFilteredProducts([]);
        // No toast for empty results, only for errors
      }

    } catch (error) {
      if (signal?.aborted) return;

      toast.error(t('search_failed') || 'Failed to search products');
      setFilteredProducts([]);
    } finally {
      // Always reset the search in progress flag
      searchInProgressRef.current = false;
    }
  }, [products, t, mapProductStructure, publicSearch, handleOpenProductDialog]);

  // ✅ Debounced search effect — cancels previous pending API call on each keystroke
  useEffect(() => {
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
    }

    const controller = new AbortController();
    searchAbortRef.current = controller;

    const timer = setTimeout(() => {
      if (searchQuery.trim()) {
        handleSearch(searchQuery, controller.signal);
      } else {
        // Search cleared — reload current category products
        if (selectedCategory) {
          const page = categoryPages[selectedCategory.id] || 1;
          const cached = categoryProductsCache[selectedCategory.id]?.[page];
          if (cached) {
            setProducts(cached);
            setFilteredProducts(cached);
          } else {
            fetchCategoryProducts(selectedCategory.id, page, selectedCategory);
          }
        }
      }
    }, 1000);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // ⚠️ IMPORTANT: handleSearch is NOT in dependencies to prevent infinite loop
    
  }, [searchQuery, selectedCategory, categoryPages, categoryProductsCache]);

  // ✅ Add toggleFullscreen function after state declarations
  const toggleFullscreen = () => {
    const elem = posContainerRef.current;
    
    if (!document.fullscreenElement && 
        !document.webkitFullscreenElement && 
        !document.mozFullScreenElement && 
        !document.msFullscreenElement) {
      // Enter fullscreen
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  const totalCartItems = cart.reduce((sum, item) => sum + item.Quantity, 0);
  const blocker = useUnsavedChanges(cart.length > 0);

  return (
    <POSLayout
      categories={categories}
      selectedCategory={selectedCategory}
      onSelectCategory={handleCategorySelect}
    >
      {/* Main Content */}
      <Box 
        ref={posContainerRef}
        sx={{ 
          display: 'flex', 
          height: isFullscreen ? '100vh' : '100%',
          width: '100%',
          position: isFullscreen ? 'fixed' : 'static',
          top: isFullscreen ? 0 : 'auto',
          left: isFullscreen ? 0 : 'auto',
          right: isFullscreen ? 0 : 'auto',
          bottom: isFullscreen ? 0 : 'auto',
          zIndex: isFullscreen ? 9999 : 'auto',
          overflow: 'hidden',
        }}
      >
        {/* Product Grid */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          overflow: 'hidden', 
          minWidth: 0,
          height: '100%',
        }}>
            {/* Search and Controls Bar */}
            <Paper
              elevation={0}
              sx={{
                m: 0,
                p: 1,
                px: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'white',
                borderRadius: 0,
                borderBottom: '1px solid #e0e0e0',
                flexShrink: 0,
                position: 'relative',
                zIndex: 2,
              }}
            >
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleOpenProductDialog()}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {t('add_product') || 'Add Product'}
              </Button>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignSelf: 'flex-start', flex: 1, minWidth: 150 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder={publicSearch ? (t('search_barcode_sku') || 'Search with Barcode or SKU...') : (t('search_products') || 'Search products...')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {publicSearch ? (
                          <FontAwesomeIcon icon={faBarcode} style={{ fontSize: '1.35rem' }} />
                        ) : <SearchIcon />}
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearchQuery('')}
                          edge="end"
                          aria-label="clear search"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
                {publicSearch && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        position: 'static',
                        marginTop: 1,
                      whiteSpace: 'normal',
                      width: '100%',
                      maxWidth: '100%',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 0.5,
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      lineHeight: 1.35,
                      pointerEvents: 'none',
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: '1rem', mt: '1px', flexShrink: 0 }} />
                    <Box component="span">
                      Search the product in the public database using barcode or SKU to save time. If the product is found, its details will be filled in automatically.
                    </Box>
                  </Typography>
                )}
              </Box>
              <Tooltip title={t('public_search_tooltip') || 'Search product by barcode or SKU to add new product'}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={publicSearch}
                      onChange={(e) => {
                        setPublicSearch(e.target.checked);
                        setSearchQuery(''); // Clear search when toggling
                        setSearchErrorMessage(''); // Clear error when toggling
                      }}
                      size="small"
                    />
                  }
                  label={t('public_search') || 'Public Search'}
                  sx={{ ml: 0, whiteSpace: 'nowrap', flexShrink: 0 }}
                />
              </Tooltip>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title={isFullscreen ? t('exit_fullscreen') : t('enter_fullscreen')}>
                  <IconButton
                    onClick={toggleFullscreen}
                    size="medium"
                  >
                    {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                  </IconButton>
                </Tooltip>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                  {receiptNumber}
                </Typography>
              </Box>
            </Paper>
          
          {/* Search Error Message Display */}
          {searchErrorMessage && (
            <Paper
              elevation={0}
              sx={{
                m: 0,
                px: 2,
                py: 1,
                bgcolor: '#fff3e0',
                borderRadius: 0,
                borderBottom: '1px solid #ffb74d',
                flexShrink: 0,
              }}
            >
              <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 500 }}>
                {searchErrorMessage}
              </Typography>
            </Paper>
          )}
          
          {/* ✅ Pagination Controls - Top Right */}
          {selectedCategory && selectedCategory.totalPages > 1 && (
            <Paper
              elevation={0}
              sx={{
                m: 0,
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: 'white',
                borderRadius: 0,
                borderBottom: '1px solid #e0e0e0',
                flexShrink: 0,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {selectedCategory.name}
                <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                  ({selectedCategory.count} {t('products') || 'products'})
                </Typography>
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  {t('page') || 'Page'} {categoryPages[selectedCategory.id] || 1} / {selectedCategory.totalPages}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title={t('previous_page') || 'Previous'}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={(categoryPages[selectedCategory.id] || 1) <= 1}
                        onClick={() => handleCategoryPageChange(selectedCategory.id, 'prev')}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          width: 32,
                          height: 32,
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          },
                          '&:disabled': {
                            bgcolor: 'action.disabledBackground',
                            color: 'action.disabled',
                          },
                        }}
                      >
                        <ChevronLeftIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  
                  <Tooltip title={t('next_page') || 'Next'}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={(categoryPages[selectedCategory.id] || 1) >= selectedCategory.totalPages}
                        onClick={() => handleCategoryPageChange(selectedCategory.id, 'next')}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          width: 32,
                          height: 32,
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          },
                          '&:disabled': {
                            bgcolor: 'action.disabledBackground',
                            color: 'action.disabled',
                          },
                        }}
                      >
                        <ChevronRightIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </Paper>
          )}
          
          {/* Product Grid */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2, pt: 1, bgcolor: '#f8f9fa' }}>
          <ProductGrid
            products={filteredProducts}
            loading={loading}
            onSelectProduct={addToCart}
            showCustomProduct={Boolean(
              !searchQuery.trim() &&
              selectedCategory &&
              categories[0]?.id === selectedCategory.id
            )}
            showRepairTicket={canCreateRepairTicket}
            onCustomProductClick={handleOpenProductDialog}
            onRepairTicketClick={() => setRepairTicketDialogOpen(true)}
            isFullscreen={isFullscreen}
            fullscreenContainer={posContainerRef.current}
            cart={cart}
          />
          </Box>
        </Box>

        {/* Cart Panel */}
        {(isMobile || isTablet) ? (
          <>
            <Zoom in={true}>
              <Fab
                color="primary"
                onClick={() => setCartDrawerOpen(true)}
                sx={{
                  position: 'fixed',
                  bottom: { xs: 16, sm: 24 },
                  right: { xs: 16, sm: 24 },
                  zIndex: 1000,
                }}
              >
                <Badge badgeContent={totalCartItems} color="error" max={99}>
                  <ShoppingCartIcon />
                </Badge>
              </Fab>
            </Zoom>

            <Drawer
              anchor="right"
              open={cartDrawerOpen}
              onClose={() => setCartDrawerOpen(false)}
              container={isFullscreen ? posContainerRef.current : undefined}
              style={{ position: isFullscreen ? 'absolute' : undefined }}
              PaperProps={{
                sx: { width: { xs: '100%', sm: 480 } }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <IconButton onClick={() => setCartDrawerOpen(false)} sx={{ mr: 1 }}>
                  <ChevronRightIcon />
                </IconButton>
                <Typography variant="h6" sx={{ flex: 1 }}>
                  {t('cart') || 'Cart'} ({totalCartItems})
                </Typography>
              </Box>
              <CartPanel
                cart={cart}
                customer={customer}
                walkInCustomerName={walkInCustomerName}
                isTvaIncluded={isTvaIncluded}
                selectedPaymentMethod={selectedPaymentMethod}
                paymentMethods={sortedPaymentMethods}
                paidAmount={paidAmount}
                receiptNumber={receiptNumber}
                discount={discount}
                customVATRate={customVATRate}
                vatRates={vatRates}
                notes={notes}
                useMultiplePayments={useMultiplePayments}
                paymentMethodsArray={paymentMethods}
                openDrawerOnPrint={openDrawerOnPrint}
                onUpdateQuantity={updateCartQuantity}
                onUpdatePrice={updateCartPrice}
                onRemoveItem={removeFromCart}
                onClearCart={clearCart}
                onSelectCustomer={() => setCustomerDialogOpen(true)}
                onWalkInNameChange={setWalkInCustomerName}
                onPaymentMethodChange={setSelectedPaymentMethod}
                onPaidAmountChange={setPaidAmount}
                onPreview={handlePreview}
                onPrint={handlePrint}
                onApplyDiscount={handleApplyDiscount}
                onRemoveDiscount={handleRemoveDiscount}
                onApplyVAT={handleApplyVAT}
                onToggleTvaIncluded={handleToggleTvaIncluded}
                onOpenNotes={() => setNotesDialogOpen(true)}
                onOpenSerialNumber={handleOpenSerialNumberDialog}
                onToggleMultiplePayments={setUseMultiplePayments}
                onPaymentMethodsChange={setPaymentMethods}
                onOpenCashDrawer={handleOpenCashDrawer}
                onToggleDrawerOnPrint={setOpenDrawerOnPrint}
                onOpenCheckout={() => setCheckoutDialogOpen(true)}
              />
            </Drawer>
          </>
        ) : (
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexShrink: 0,
              width: cartCollapsed ? 28 : { xs: '100%', md: 460, lg: 480 },
              transition: 'width 0.3s ease',
              overflow: 'hidden',
              height: '100%',
            }}
          >
            <Tooltip title={cartCollapsed ? 'Expand cart' : 'Collapse cart'} placement="left">
              <IconButton
                size="small"
                onClick={() => setCartCollapsed(prev => !prev)}
                sx={{
                  position: 'absolute',
                  left: -12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 20,
                  bgcolor: 'white',
                  border: '1px solid',
                  borderColor: 'divider',
                  width: 24,
                  height: 24,
                  boxShadow: 2,
                  '&:hover': { bgcolor: '#f0f0f0' },
                }}
              >
                {cartCollapsed
                  ? <ChevronLeftIcon sx={{ fontSize: 16 }} />
                  : <ChevronRightIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
            <Box sx={{ width: { xs: '100%', md: 460, lg: 480 }, flexShrink: 0, height: '100%' }}>
              <CartPanel
                cart={cart}
                customer={customer}
                walkInCustomerName={walkInCustomerName}
                isTvaIncluded={isTvaIncluded}
                selectedPaymentMethod={selectedPaymentMethod}
                paymentMethods={sortedPaymentMethods}
                paidAmount={paidAmount}
                receiptNumber={receiptNumber}
                discount={discount}
                customVATRate={customVATRate}
                vatRates={vatRates}
                notes={notes}
                useMultiplePayments={useMultiplePayments}
                paymentMethodsArray={paymentMethods}
                openDrawerOnPrint={openDrawerOnPrint}
                onUpdateQuantity={updateCartQuantity}
                onUpdatePrice={updateCartPrice}
                onRemoveItem={removeFromCart}
                onClearCart={clearCart}
                onSelectCustomer={() => setCustomerDialogOpen(true)}
                onWalkInNameChange={setWalkInCustomerName}
                onPaymentMethodChange={setSelectedPaymentMethod}
                onPaidAmountChange={setPaidAmount}
                onPreview={handlePreview}
                onPrint={handlePrint}
                onApplyDiscount={handleApplyDiscount}
                onRemoveDiscount={handleRemoveDiscount}
                onApplyVAT={handleApplyVAT}
                onToggleTvaIncluded={handleToggleTvaIncluded}
                onOpenNotes={() => setNotesDialogOpen(true)}
                onOpenSerialNumber={handleOpenSerialNumberDialog}
                onToggleMultiplePayments={setUseMultiplePayments}
                onPaymentMethodsChange={setPaymentMethods}
                onOpenCashDrawer={handleOpenCashDrawer}
                onToggleDrawerOnPrint={setOpenDrawerOnPrint}
                onOpenCheckout={() => setCheckoutDialogOpen(true)}
              />
            </Box>
          </Box>
        )}
      </Box>
      {/* End of posContainerRef Box */}

      {/* Checkout Dialog */}
      <CheckoutDialog
        open={checkoutDialogOpen}
        onClose={() => setCheckoutDialogOpen(false)}
        cart={cart}
        customer={customer}
        walkInCustomerName={walkInCustomerName}
        isTvaIncluded={isTvaIncluded}
        paymentMethods={sortedPaymentMethods}
        selectedPaymentMethod={selectedPaymentMethod}
        paidAmount={paidAmount}
        notes={notes}
        useMultiplePayments={useMultiplePayments}
        paymentMethodsArray={paymentMethods}
        discount={discount}
        customVATRate={customVATRate}
        receiptNumber={receiptNumber}
        onWalkInNameChange={setWalkInCustomerName}
        onFinalize={handleCheckoutFinalize}
        onConvertToInvoice={(localData) => handleConvertToInvoice(localData)}
        onCustomerChange={setCustomer}
        onOpenCustomerDialog={() => {
          customerFromCheckoutRef.current = true;
          setCheckoutDialogOpen(false);
          setCustomerDialogOpen(true);
        }}
        vatRates={vatRates}
        openDrawerOnPrint={openDrawerOnPrint}
        onApplyVAT={handleApplyVAT}
        onApplyDiscount={handleApplyDiscount}
        onRemoveDiscount={handleRemoveDiscount}
        container={isFullscreen ? posContainerRef.current : undefined}
      />

      {/* Dialogs */}
      <PrintOptionsDialog
        open={printOptionsOpen}
        onClose={() => setPrintOptionsOpen(false)}
        onSelectFormat={handleFormatSelection}
        container={isFullscreen ? posContainerRef.current : undefined}
        style={{ position: isFullscreen ? 'absolute' : undefined }}
      />

      <CustomerDialog
        open={customerDialogOpen}
        onClose={() => setCustomerDialogOpen(false)}
        onSelectCustomer={(selectedCustomer) => {
          setCustomer(selectedCustomer);
          setCustomerDialogOpen(false);
          if (customerFromCheckoutRef.current) {
            customerFromCheckoutRef.current = false;
            setCheckoutDialogOpen(true);
          }
          if (customerFromRepairRef.current) {
            customerFromRepairRef.current = false;
            setRepairTicketDialogOpen(true);
          }
        }}
        onCreateNewCustomer={() => {
          // keep customerFromCheckoutRef/customerFromRepairRef so handleCustomerCreated can reopen the dialog
          setCustomerDialogOpen(false);
          setAddCustomerDialogOpen(true);
        }}
        currentCustomer={customer}
        container={posContainerRef.current}
        style={{ position: 'absolute' }}
      />

      {currentFormat === 'a4' ? (
        <A4PreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          htmlContent={previewHtml}
          onPrint={() => printDocument(previewHtml, currentFormat)}
          container={isFullscreen ? posContainerRef.current : undefined}
          style={{ position: isFullscreen ? 'absolute' : undefined }}
        />
      ) : (
        <PreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          htmlContent={previewHtml}
          onPrint={() => printDocument(previewHtml, currentFormat)}
          format="receipt"
          container={isFullscreen ? posContainerRef.current : undefined}
          style={{ position: isFullscreen ? 'absolute' : undefined }}
        />
      )}

      <AddProductDialog
        open={productDialogOpen}
        onClose={() => {
          setProductDialogOpen(false);
          setInitialProductData(null); // Clear initial data on close
        }}
        onProductCreated={handleProductCreated}
        suppliersFromStore={suppliersFromStore}
        brandsFromStore={brandsFromStore}
        categoriesFromStore={categoriesFromStore}
        onSupplierCreated={(supplier) => dispatch(addSupplierToStore(supplier))}
        onBrandCreated={(brand) => dispatch(addBrandToStore(brand))}
        onCategoryCreated={(category) => dispatch(addCategoryToStore(category))}
        initialData={initialProductData}
      />

      <AddCustomerDialog
        open={addCustomerDialogOpen}
        onClose={() => setAddCustomerDialogOpen(false)}
        onCustomerCreated={handleCustomerCreated}
        optionalFields={{ email: true, phone: true }}
      />

      {/* ✅ Notes Dialog */}
      <Dialog
        open={notesDialogOpen}
        onClose={() => setNotesDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        container={isFullscreen ? posContainerRef.current : undefined}
        style={{ position: isFullscreen ? 'absolute' : undefined }}
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('add_notes') || 'Add Notes'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            placeholder={t('enter_notes_placeholder') || 'Enter notes for the receipt (max 300 characters)...'}
            value={notes}
            onChange={(e) => {
              const value = e.target.value;
              if (value.length <= 300) {
                setNotes(value);
              }
            }}
            inputProps={{
              maxLength: 300,
            }}
            helperText={`${notes.length}/300 ${t('characters') || 'characters'}`}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNotesDialogOpen(false)} color="inherit">
            {t('cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={() => handleSaveNotes(notes)}
            variant="contained"
            color="primary"
          >
            {t('save') || 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Serial Number Dialog */}
      <Dialog
        open={serialNumberDialog.open}
        onClose={handleCloseSerialNumberDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <QrCodeIcon />
          {t('serialNumber') || 'Serial Number'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {serialNumberDialog.item && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                {t('product') || 'Product'}:
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {serialNumberDialog.item.Name}
              </Typography>
            </Paper>
          )}
          <TextField
            autoFocus
            fullWidth
            label={t('serialNumber') || 'Serial Number'}
            value={serialNumberDialog.serialNumber}
            onChange={(e) =>
              setSerialNumberDialog((prev) => ({
                ...prev,
                serialNumber: e.target.value,
              }))
            }
            placeholder={t('enterSerialNumber') || 'Enter serial number'}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseSerialNumberDialog} color="inherit">
            {t('cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleSaveSerialNumber}
            variant="contained"
            color="primary"
          >
            {t('save') || 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Repair Ticket Dialog */}
      <RepairTicketDialog
        open={repairTicketDialogOpen}
        onClose={() => setRepairTicketDialogOpen(false)}
        onSubmit={handleRepairTicketSubmit}
        paymentMethods={sortedPaymentMethods}
        vatRates={vatRates}
        customer={customer}
        onOpenCustomerDialog={() => {
          // Hide repair dialog and open customer selection
          customerFromRepairRef.current = true;
          setRepairTicketDialogOpen(false);
          setCustomerDialogOpen(true);
        }}
        container={isFullscreen ? posContainerRef.current : undefined}
        storeInfo={storeInfo} // ✅ Pass store info for preview
        categoriesData={rawCategoriesWithProducts} // ✅ Pass categories data to avoid duplicate API call
        categoryProductsCache={categoryProductsCache} // ✅ Pass products cache
      />

      <UnsavedChangesDialog
        open={blocker.state === 'blocked'}
        onDiscard={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
      />
    </POSLayout>
  );
};

export default PosListPage;